import type { DashboardWidgetWire } from '../models/widget.types'

import type { ReportDatePickerHydration } from '@/features/reports'
import type { CompareModeId, DatePresetId } from '@/shared/ui'

export const WIDGET_DATE_PRESET_IDS = [
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Last Month',
  'This Year',
  'Last Year',
  'Last 7 Days',
  'Last 14 Days',
  'Last 28 Days',
  'Last 30 Days',
  'Last 60 Days',
  'Last 90 Days',
  'Maximum',
  'Custom',
] as const satisfies readonly DatePresetId[]

export const WIDGET_COMPARE_MODE_IDS = [
  'Previous Period',
  'Previous Week',
  'Previous Month',
  'Previous Year',
  'Custom',
] as const satisfies readonly CompareModeId[]

const DATE_PRESET_IDS: ReadonlySet<string> = new Set<DatePresetId>(WIDGET_DATE_PRESET_IDS)
const COMPARE_MODE_IDS: ReadonlySet<string> = new Set(WIDGET_COMPARE_MODE_IDS)

function record(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/** Custom primary dates as the query fragment module 08's request layer reads. */
export function widgetDefaultPostData(wire: DashboardWidgetWire): string {
  const params = new URLSearchParams()
  const primary = wire.selectedDate === 'Custom' ? record(wire.customDate) : undefined
  const activeCustomCompare = wire.compareEnabled === true && wire.compareType === 'Custom'
  const compare = activeCustomCompare ? record(wire.customCompareDate) : undefined
  const start = text(primary?.date_start)
  const end = text(primary?.date_end)
  if (!start || !end) return ''
  params.set('date_start', start)
  params.set('date_end', end)
  const compareStart = activeCustomCompare
    ? (text(compare?.compare_date_start) ?? text(primary?.compare_date_start))
    : undefined
  const compareEnd = activeCustomCompare
    ? (text(compare?.compare_date_end) ?? text(primary?.compare_date_end))
    : undefined
  if (activeCustomCompare && compareStart && compareEnd) {
    params.set('compare_date_start', compareStart)
    params.set('compare_date_end', compareEnd)
  }
  return `&${params.toString()}`
}

export function widgetDatePreset(wire: DashboardWidgetWire): DatePresetId | undefined {
  const selected = text(wire.selectedDate)
  return selected && DATE_PRESET_IDS.has(selected) ? (selected as DatePresetId) : undefined
}

/** Typed module-08 hydration for persisted widget-owned date and comparison state. */
export function widgetDatePickerHydration(
  wire: DashboardWidgetWire,
): ReportDatePickerHydration | undefined {
  const presetId = widgetDatePreset(wire)
  const primary = presetId === 'Custom' ? record(wire.customDate) : undefined
  const activeCustomCompare = wire.compareEnabled === true && wire.compareType === 'Custom'
  const compare = activeCustomCompare ? record(wire.customCompareDate) : undefined
  const compareValue = text(wire.compareType)
  const compareType =
    compareValue && COMPARE_MODE_IDS.has(compareValue) ? (compareValue as CompareModeId) : undefined
  if (
    presetId === undefined &&
    typeof wire.compareEnabled !== 'boolean' &&
    compareType === undefined &&
    primary === undefined &&
    compare === undefined
  )
    return undefined
  return {
    ...(presetId ? { presetId } : {}),
    ...(typeof wire.compareEnabled === 'boolean' ? { compareEnabled: wire.compareEnabled } : {}),
    ...(compareType ? { compareType } : {}),
    start: text(primary?.date_start) ?? null,
    end: text(primary?.date_end) ?? null,
    compareStart: activeCustomCompare
      ? (text(compare?.compare_date_start) ?? text(primary?.compare_date_start) ?? null)
      : null,
    compareEnd: activeCustomCompare
      ? (text(compare?.compare_date_end) ?? text(primary?.compare_date_end) ?? null)
      : null,
  }
}
